import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Link } from '../../router/Router';
import { validateEmail } from '../../utils/errors';
import { Mail, AlertCircle, CheckCircle, ArrowLeft, Send } from 'lucide-react';

export const ForgotPassword: React.FC = () => {
  const { resetPassword, isLoading, error, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setClientError(null);
    clearError();
    setIsSuccess(false);

    if (!email.trim()) {
      setClientError('يرجى إدخال البريد الإلكتروني.');
      return;
    }

    if (!validateEmail(email)) {
      setClientError('صيغة البريد الإلكتروني غير صحيحة (مثال: student@example.com).');
      return;
    }

    const success = await resetPassword(email.trim());
    if (success) {
      setIsSuccess(true);
    }
  };

  const activeError = clientError || error?.messageAr;

  return (
    <div className="auth-page-wrapper" dir="rtl">
      <div className="auth-card">
        {/* Header */}
        <div className="auth-card-header">
          <div className="auth-brand-badge" style={{ padding: '0', overflow: 'hidden', width: '56px', height: '56px', borderRadius: '16px', margin: '0 auto 1rem' }}>
            <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <h1 className="auth-title">استعادة كلمة المرور</h1>
          <p className="auth-subtitle">
            أدخل بريدك الإلكتروني المسجل وسنرسل لك رابطًا آمنًا لإعادة تعيين كلمة المرور.
          </p>
        </div>

        {/* Success Alert */}
        {isSuccess ? (
          <div className="auth-success-banner" role="status">
            <CheckCircle className="auth-success-icon" size={24} />
            <div>
              <h4>تم إرسال الرابط بنجاح!</h4>
              <p>
                تم إرسال رابط إعادة تعيين كلمة المرور إلى <strong>{email}</strong>. يرجى فحص صندوق الوارد (أو مجلد الرسائل غير المرغوب فيها Spam).
              </p>
            </div>
          </div>
        ) : null}

        {/* Error Alert */}
        {!isSuccess && activeError && (
          <div className="auth-error-banner" role="alert">
            <AlertCircle className="auth-error-icon" size={20} />
            <span>{activeError}</span>
          </div>
        )}

        {/* Form */}
        {!isSuccess ? (
          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="form-group">
              <label htmlFor="reset-email" className="form-label">
                البريد الإلكتروني
              </label>
              <div className="input-with-icon">
                <Mail className="input-icon" size={18} />
                <input
                  id="reset-email"
                  type="email"
                  className="form-input"
                  placeholder="student@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (clientError) setClientError(null);
                  }}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary auth-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="btn-loading-content">
                  <span className="spinner-inline"></span>
                  جاري الإرسال...
                </span>
              ) : (
                <span className="btn-normal-content">
                  <Send size={18} />
                  إرسال رابط إعادة تعيين كلمة المرور
                </span>
              )}
            </button>
          </form>
        ) : (
          <div className="auth-action-box">
            <Link to="/login" className="btn-primary auth-submit-btn">
              العودة لتسجيل الدخول
            </Link>
          </div>
        )}

        {/* Footer */}
        <div className="auth-card-footer">
          <p>
            تذكرت كلمة المرور؟{' '}
            <Link to="/login" className="auth-switch-link">
              تسجيل الدخول
            </Link>
          </p>
          <div className="back-home-link-box">
            <Link to="/" className="back-home-link">
              <ArrowLeft size={16} />
              العودة للرئيسية
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
